import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subject } from 'rxjs';

// ─── Shared interfaces ────────────────────────────────────────────────────────

export interface IMediaItem {
  _id: string;
  url: string;
  urlSmall: string;
  urlMedium: string;
  urlLarge: string;
  type: 'image' | 'video';
}

export interface IUserSnippet {
  _id: string;
  displayName: string;
  userName: string;
  profileImage: { urlSmall?: string; urlMedium?: string; urlLarge?: string } | null;
  status?: string;
}

export interface ICollaboration {
  user: IUserSnippet;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  invitedAt: string;
  respondedAt?: string;
}

export type ReactionType = 'LIKE' | 'HEART' | 'HUG' | 'WOW' | 'SAD' | 'HAHA';

export interface IPostInfo {
  _id: string;
  backgroundPattern?: string;   // pattern.css id e.g. "p-dots-pink"
  backgroundBg?: string;   // CSS color/gradient
  location?: string;
  audience: PostAudienceType;
  commentsDisabled?: boolean;
  mentions: IUserSnippet[];
  collaborations: ICollaboration[];
}

export interface IReactionSummary {
  total: number;
  counts: {
    LIKE: number;
    HEART: number;
    HUG: number;
    WOW: number;
    SAD: number;
    HAHA: number;
  };
  myReaction: string | null;
  recentUserName: string | null;
  topTypes: string[];
}

export interface IPost {
  _id: string;
  title: string;       // caption stored here
  type: 'POST';
  user: IUserSnippet;
  postInfo: IPostInfo | null;
  media: IMediaItem[];
  hashtags: string[];
  numberOfLikes: number;
  numberOfComments: number;
  numberOfShares: number;
  engagementScore: number;
  reactions: IReactionSummary;
  isMyPost: boolean;
  isFollowed: boolean;
  friendship: { status: string; requestId?: string };
  createdAt: string;
  updatedAt: string;
  isRemovedByAdmin?: boolean;
}

export interface IPaginatedPosts {
  items: IPost[];
  total: number;
  page: number;
  limit: number;
}

export type PostAudienceType =
  | 'EVERYONE'
  | 'FOLLOWERS_FRIENDS'
  | 'FRIENDS_ONLY'
  | 'ONLY_ME';

export interface ICreatePostPayload {
  caption?: string;
  backgroundPattern?: string;
  backgroundBg?: string;
  location?: string;
  audience?: PostAudienceType;
  hashtags?: string;
  collaboratorIds?: string[];   // Changed to array
  mentions?: string[];   // Changed to array
  files?: File[];
  commentsDisabled?: boolean;
  removedMediaIds?: string[];   // IDs of existing media to delete during edit
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class PostService {

  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private baseUrl = `${environment.apiUrl}`;

  private refreshPosts$ = new Subject<void>();
  onRefreshPosts$ = this.refreshPosts$.asObservable();

  triggerRefresh(): void {
    this.refreshPosts$.next();
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  createPost(payload: ICreatePostPayload): Observable<HttpEvent<any>> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    const formData = new FormData();
    if (payload.caption) formData.append('caption', payload.caption);
    if (payload.backgroundPattern) formData.append('backgroundPattern', payload.backgroundPattern);
    if (payload.backgroundBg) formData.append('backgroundBg', payload.backgroundBg);
    if (payload.location) formData.append('location', payload.location);
    if (payload.audience) formData.append('audience', payload.audience);
    if (payload.hashtags) formData.append('hashtags', payload.hashtags);

    // Handle arrays properly
    if (payload.collaboratorIds?.length) {
      formData.append('collaboratorIds', payload.collaboratorIds.join(','));
    }
    if (payload.mentions?.length) {
      formData.append('mentions', payload.mentions.join(','));
    }

    if (payload.commentsDisabled) {
      formData.append('commentsDisabled', 'true');
    }

    for (const file of payload.files ?? []) {
      formData.append('files', file);
    }

    return this.http.post(`${this.baseUrl}/posts`, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  // ── Read ───────────────────────────────────────────────────────────────────

  /**
   * Runs during SSR too (no isPlatformBrowser guard) — the post/:id route is
   * RenderMode.Server specifically so this data (and the SeoService tags built
   * from it) is present in the initial HTML for share-preview crawlers.
   */
  getPost(postId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/posts/${postId}`);
  }

  getPosts(page = 1, limit = 10) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    return this.http.get(`${this.baseUrl}/posts`, { params });
  }

  getUserPosts(userName: string, page = 1, limit = 10, collabOnly = false): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (collabOnly) params = params.set('collabOnly', 'true');
    return this.http.get(`${this.baseUrl}/posts/user/${userName}`, { params });
  }

  getFeaturedPosts(): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.get(`${this.baseUrl}/posts/featured`);
  }

  // ── Update (owner only) ───────────────────────────────────────────────────

  updatePost(postId: string, payload: Partial<ICreatePostPayload>): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    const formData = new FormData();
    if (payload.caption !== undefined) formData.append('caption', payload.caption);
    if (payload.backgroundPattern !== undefined) formData.append('backgroundPattern', payload.backgroundPattern);
    if (payload.backgroundBg !== undefined) formData.append('backgroundBg', payload.backgroundBg);
    if (payload.location !== undefined) formData.append('location', payload.location);
    if (payload.audience !== undefined) formData.append('audience', payload.audience);
    if (payload.hashtags !== undefined) formData.append('hashtags', payload.hashtags);
    // NOTE: must send this even when the array is EMPTY (not just when it has
    // items) — an empty list is how the caller says "I removed every
    // collaborator/tag". Using `?.length` here silently drops that case
    // because `[].length` is falsy, so the backend never receives the
    // "clear everything" signal and the last collaborator/tag never gets
    // removed. Same reasoning as removedMediaIds below.
    if (payload.collaboratorIds !== undefined) formData.append('collaboratorIds', payload.collaboratorIds.join(','));
    if (payload.mentions !== undefined) formData.append('mentions', payload.mentions.join(','));
    if (payload.commentsDisabled !== undefined) formData.append('commentsDisabled', String(payload.commentsDisabled));
    // NOTE: must send this even when the array is EMPTY (not just when it has
    // items) — an empty list is how the caller says "delete all existing
    // media" (e.g. switching a media post to a background post). Using
    // `?.length` here would silently drop that case because `[].length` is
    // falsy, so the backend never received the "clear everything" signal.
    if (payload.removedMediaIds !== undefined) formData.append('removedMediaIds', payload.removedMediaIds.join(','));
    for (const file of payload.files ?? []) formData.append('files', file);

    return this.http.patch(`${this.baseUrl}/posts/${postId}`, formData);
  }

  // ── Leave Collaboration (collaborator only) ────────────────────────────────

  leaveCollaboration(postId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete(`${this.baseUrl}/posts/${postId}/collaborate/leave`);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  deletePost(postId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.delete(`${this.baseUrl}/posts/${postId}`);
  }

  // ── Toggle comments ────────────────────────────────────────────────────────

  toggleComments(postId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/posts/${postId}/toggle-comments`, {});
  }

  // ── Collaboration ──────────────────────────────────────────────────────────

  acceptCollaboration(postId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/posts/${postId}/collaborate/accept`, {});
  }

  declineCollaboration(postId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.patch(`${this.baseUrl}/posts/${postId}/collaborate/decline`, {});
  }

  reactToPost(contentId: string, type: ReactionType) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/reactions/${contentId}`, { type });
  }

  getReactions(contentId: string, type?: ReactionType) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    return this.http.get(`${this.baseUrl}/reactions/${contentId}`, { params });
  }
}